import styled, { css, type DefaultTheme } from 'styled-components';
import Colours from '/shared/styled/Colours';
import { baseFontWeight } from '/shared/styled/GlobalStyle/GlobalStyle';

interface Props {
  theme?: DefaultTheme;
  $type?: 'h0' | 'h1' | 'h1.5' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | null;
  $noMargin?: boolean;
  $green?: boolean;
  $white?: boolean;
  $midGrey?: boolean;
  $faded?: boolean;
  $hasExtraBottomMargin?: boolean;
  $hasLessTopMargin?: boolean;
  $hasExtraTopMargin?: boolean;
  $hasSuperTopMargin?: boolean;
  $hasSuperBottomMargin?: boolean;
  $hasLightFont?: boolean;
  $semibold?: boolean;
  $semiBold?: boolean;
  $bold?: boolean;
  $inline?: boolean;
}

function h0Style({ $type = null }: Props) {
  if ($type === 'h0') {
    return css`
      font-size: 32px;
      line-height: 48px;
    `;
  }
}

function h1Style({ $type = null }: Props) {
  if ($type === 'h1') {
    return css`
      font-size: 1.75rem;
      line-height: 1.5;
    `;
  }
}

function h15Style({ $type = null }: Props) {
  if ($type === 'h1.5') {
    return css`
      font-size: 1.45rem;
      line-height: 1.3;
      font-weight: 500;
    `;
  }
}

function h2Style({ $type = null }: Props) {
  if ($type === 'h2') {
    return css`
      font-size: 1.33333rem;
      line-height: 1.5;
    `;
  }
}

function h3Style({ $type = null }: Props) {
  if ($type === 'h3') {
    return css`
      font-size: 1.25rem;
    `;
  }
}

function h4Style({ $type = null }: Props) {
  if ($type === 'h4') {
    return css`
      font-size: 0.9333rem;
    `;
  }
}

function h5Style({ $type = null }: Props) {
  if ($type === 'h5') {
    return css`
      font-size: 20px;
      line-height: 30px;
      letter-spacing: 0.1px;
    `;
  }
}

function h6Style({ $type = null }: Props) {
  if ($type === 'h6') {
    return css`
      font-size: 16px;
    `;
  }
}

function noMarginStyle({ $noMargin = false }: Props) {
  if ($noMargin) {
    return css`
      margin: 0;
    `;
  }
}

function greenStyle({ $green = false, theme }: Props) {
  if ($green) {
    return css`
      color: ${Colours.green};
      font-weight: 500;
      font-size: 1.3333rem;
    `;
  }
}

function whiteStyle({ $white = false, theme }: Props) {
  if ($white) {
    return css`
      color: ${Colours.white};
      font-weight: 500;
      font-size: 1.3333rem;
    `;
  }
}

function midGreyStyle({ $midGrey = false, theme }: Props) {
  if ($midGrey) {
    return css`
      color: ${Colours.grey};
    `;
  }
}

function fadedStyle({ $faded = false }: Props) {
  if ($faded) {
    return css`
      color: ${Colours.greyLight6};
    `;
  }
}

function hasExtraBottomMarginStyle({ $hasExtraBottomMargin = false }: Props) {
  if ($hasExtraBottomMargin) {
    return css`
      margin-bottom: 2.5rem !important;
    `;
  }
}

function hasLessTopMarginStyle({ $hasLessTopMargin = false }: Props) {
  if ($hasLessTopMargin) {
    return css`
      margin-top: 1.5rem;
    `;
  }
}

function hasExtraTopMarginStyle({ $hasExtraTopMargin = false }: Props) {
  if ($hasExtraTopMargin) {
    return css`
      margin-top: 2.5rem;
    `;
  }
}

function hasSuperTopMarginStyle({ $hasSuperTopMargin = false }: Props) {
  if ($hasSuperTopMargin) {
    return css`
      margin-top: 5rem;
    `;
  }
}

function hasSuperBottomMarginStyle({ $hasSuperBottomMargin = false }: Props) {
  if ($hasSuperBottomMargin) {
    return css`
      margin-bottom: 5rem;
    `;
  }
}

function hasLightFontStyle({ $hasLightFont = false }: Props) {
  if ($hasLightFont) {
    return css`
      font-weight: 300;
    `;
  }
}

function isSemibold({ $semibold = false }: Props) {
  if ($semibold) {
    return css`
      font-weight: 400;
    `;
  }
}

function isBold({ $bold = false }: Props) {
  if ($bold) {
    return css`
      font-weight: 500;
    `;
  }
}

function isInline({ $inline = false }: Props) {
  if ($inline) {
    return css`
      display: inline;
    `;
  }
}

export default styled.div<Props>`
  margin: 0;
  padding: 0;
  margin-bottom: 1rem;
  color: ${Colours.black};

  small {
    font-size: 0.6em;
  }

  ${greenStyle}
  ${whiteStyle}
  ${fadedStyle}
  ${midGreyStyle}
  ${h0Style}
  ${h1Style}
  ${h15Style}
  ${h2Style}
  ${h3Style}
  ${h4Style}
  ${h5Style}
  ${h6Style}
  ${noMarginStyle}
  ${hasExtraBottomMarginStyle}
  ${hasSuperBottomMarginStyle}
  ${hasExtraTopMarginStyle}
  ${hasSuperTopMarginStyle}
  ${hasLessTopMarginStyle}
  ${baseFontWeight}
  ${hasLightFontStyle}
  ${isSemibold}
  ${isBold}
  ${isInline}
`;
